/**
 * MUI Component overrides — aligned with frontend/DESIGN.md
 *
 * Square corners, flat surfaces, border-only cards, teal accent.
 */

import { alpha } from '@mui/material/styles';

const DEFAULT_PRIMARY = '#009c87';
const DEFAULT_PRIMARY_DARK = '#027A6B';
const DEFAULT_PRIMARY_CONTRAST = '#ffffff';

export const components = (portalTheme = {}) => {
  const theme = {
    primary: portalTheme.primary || DEFAULT_PRIMARY,
    primaryDark: portalTheme.primaryDark || DEFAULT_PRIMARY_DARK,
    primaryContrast: portalTheme.primaryContrast || DEFAULT_PRIMARY_CONTRAST,
    sidebar: portalTheme.sidebar || {
      background: '#ffffff',
      activeItem: '#f7f7f8',
      hoverItem: '#f7f7f8',
      activeText: DEFAULT_PRIMARY,
    },
    navbar: portalTheme.navbar || {
      background: '#ffffff',
    },
    commission: portalTheme.commission,
  };

  return ({
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        textTransform: 'none',
        fontWeight: 600,
        minHeight: 36,
        border: '1px solid transparent',
        padding: '7px 15px',
        transition: 'background-color 200ms ease, color 200ms ease, border-color 200ms ease',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
        '&:active': {
          transform: 'scale(0.98)',
          boxShadow: 'none',
        },
        '&:disabled': {
          backgroundColor: '#ECECEF',
          color: '#9797A5',
          borderColor: 'transparent',
        },
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      containedPrimary: {
        backgroundColor: theme.primary,
        color: theme.primaryContrast,
        '&:hover': {
          backgroundColor: theme.primaryDark,
        },
      },
      outlined: {
        borderWidth: 1,
        borderColor: '#ececef',
        color: '#212121',
        padding: '7px 15px',
        '&:hover': {
          borderWidth: 1,
          borderColor: '#d0d0d5',
          backgroundColor: '#f7f7f8',
        },
      },
      outlinedPrimary: {
        borderColor: theme.primary,
        color: theme.primary,
        '&:hover': {
          borderColor: theme.primaryDark,
          backgroundColor: alpha(theme.primary, 0.06),
        },
      },
      text: {
        border: '1px solid transparent',
        '&:hover': {
          backgroundColor: alpha(theme.primary, 0.06),
        },
      },
      sizeLarge: {
        padding: '9px 19px',
        fontSize: '0.9375rem',
        minHeight: 42,
      },
      sizeSmall: {
        padding: '5px 11px',
        fontSize: '0.8125rem',
        minHeight: 30,
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: 0,
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        boxShadow: 'none',
        border: '1px solid #ececef',
        transition: 'box-shadow 200ms ease',
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 24,
        '&:last-child': {
          paddingBottom: 24,
        },
      },
    },
  },

  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 0,
          transition: 'all 0.2s',
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ececef',
          },
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d0d0d5',
              borderWidth: 1,
            },
          },
          '&.Mui-focused': {
            backgroundColor: '#ffffff',
            boxShadow: '0 0 0 2px #CCE4FF',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4080FF',
              borderWidth: 1,
            },
          },
          '&.Mui-error': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ef4444',
            },
          },
        },
        '& .MuiInputLabel-root': {
          color: '#9797A5',
          '&.Mui-focused': {
            color: '#212121',
          },
          '&.Mui-error': {
            color: '#ef4444',
          },
        },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 0,
      },
    },
  },

  MuiSelect: {
    styleOverrides: {
      outlined: {
        borderRadius: 0,
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        fontWeight: 500,
        fontSize: '0.6875rem',
        letterSpacing: 0,
        border: '1px solid transparent',
        height: 22,
      },
      colorDefault: {
        backgroundColor: '#f3f4f6',
        color: '#374151',
        border: '1px solid transparent',
      },
      colorPrimary: {
        backgroundColor: '#d0f0e9',
        color: '#004a40',
        border: '1px solid transparent',
      },
      colorSuccess: {
        backgroundColor: '#d0f0e9',
        color: '#004a40',
        border: '1px solid transparent',
      },
      colorError: {
        backgroundColor: '#fde2e1',
        color: '#900b09',
        border: '1px solid transparent',
      },
      colorWarning: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        border: '1px solid transparent',
      },
      colorInfo: {
        backgroundColor: '#e0f2fe',
        color: '#075985',
        border: '1px solid transparent',
      },
    },
  },

  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-head': {
          backgroundColor: '#f5f5f5',
          color: '#212121',
          fontWeight: 700,
          fontSize: '0.75rem',
          letterSpacing: 0,
          textTransform: 'none',
          borderBottom: 'none',
          padding: '10px 16px',
        },
        '& .MuiTableCell-head:first-of-type': {
          borderTopLeftRadius: 6,
          borderBottomLeftRadius: 6,
        },
        '& .MuiTableCell-head:last-of-type': {
          borderTopRightRadius: 6,
          borderBottomRightRadius: 6,
        },
      },
    },
  },

  MuiTableContainer: {
    styleOverrides: {
      root: {
        backgroundColor: '#ffffff',
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 8,
      },
    },
  },

  MuiTableBody: {
    styleOverrides: {
      root: {
        '& .MuiTableRow-root': {
          transition: 'background-color 150ms ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
          '&:last-child td': {
            borderBottom: 'none',
          },
        },
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: '1px solid #ececef',
        padding: '12px 16px',
        fontSize: '0.875rem',
        color: '#212121',
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        backgroundImage: 'none',
      },
      elevation1: {
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      },
      elevation2: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      },
      elevation3: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        boxShadow: '0 1px 0 #ececef',
        backgroundColor: theme.navbar?.background || '#ffffff',
      },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        boxShadow: 'none',
        borderRight: '1px solid #ececef',
        backgroundColor: theme.sidebar?.background || '#ffffff',
      },
    },
  },

  MuiAvatar: {
    styleOverrides: {
      root: {
        fontWeight: 700,
        fontSize: '0.875rem',
        backgroundColor: theme.primary,
        color: theme.primaryContrast,
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: '#ececef',
      },
    },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        marginBottom: 2,
        transition: 'background-color 150ms ease',
        '&:hover': {
          backgroundColor: theme.sidebar?.hoverItem || '#f7f7f8',
        },
        '&.Mui-selected': {
          backgroundColor: theme.sidebar?.activeItem || '#f7f7f8',
          color: theme.sidebar?.activeText || theme.primary,
          '&:hover': {
            backgroundColor: theme.sidebar?.activeItem || '#f7f7f8',
          },
          '& .MuiListItemIcon-root': {
            color: theme.sidebar?.activeText || theme.primary,
          },
        },
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        fontSize: '0.875rem',
      },
      standardSuccess: {
        backgroundColor: '#d0f0e9',
        color: '#004a40',
        border: '1px solid transparent',
        '& .MuiAlert-icon': {
          color: '#004a40',
        },
      },
      standardError: {
        backgroundColor: '#fde2e1',
        color: '#900b09',
        border: '1px solid transparent',
        '& .MuiAlert-icon': {
          color: '#900b09',
        },
      },
      standardWarning: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        border: '1px solid transparent',
        '& .MuiAlert-icon': {
          color: '#92400e',
        },
      },
      standardInfo: {
        backgroundColor: '#e0f2fe',
        color: '#075985',
        border: '1px solid transparent',
        '& .MuiAlert-icon': {
          color: '#075985',
        },
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: '#212121',
        fontSize: '0.75rem',
        borderRadius: 0,
        padding: '6px 10px',
      },
      arrow: {
        color: '#212121',
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
        marginTop: 4,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
        border: '1px solid #ececef',
      },
    },
  },

  MuiPopover: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
      },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        height: 6,
        backgroundColor: '#f3f4f6',
      },
      bar: {
        borderRadius: 0,
        backgroundColor: theme.primary,
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.1)',
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        fontWeight: 700,
        fontSize: '1rem',
        color: '#212121',
        padding: '16px 24px',
        borderBottom: '1px solid #ececef',
        backgroundColor: '#ffffff',
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '32px 24px 24px',
        '.MuiDialogTitle-root + &': {
          paddingTop: '32px',
        },
        '&.MuiDialogContent-dividers': {
          borderTop: '1px solid #ececef',
          borderBottom: '1px solid #ececef',
        },
      },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '12px 24px',
        borderTop: '1px solid #ececef',
      },
    },
  },

  MuiSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
      },
      wave: {
        '&::after': {
          background: 'linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.04), transparent)',
        },
      },
    },
  },

  MuiToggleButton: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        textTransform: 'none',
        fontWeight: 500,
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: theme.primary,
        height: 2,
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        minHeight: 44,
        '&.Mui-selected': {
          color: theme.primary,
        },
      },
    },
  },
})};
